import React, { useEffect, useState, useRef } from 'react';
import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
const ServiceAreaMap = () => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstanceRef = useRef<L.Map | null>(null);
  useEffect(() => {
    if (mapRef.current && !mapInstanceRef.current) {
      // Clinton, TN coordinates: 36.1034, -84.1319
      // Zoom level 7 to include North Georgia (Atlanta area ~33.7)
      const map = L.map(mapRef.current).setView([34.9, -84.2], 7);
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
      }).addTo(map);
      // Custom marker icon
      const customIcon = L.divIcon({
        className: 'custom-marker',
        html: `<div style="background-color: #1e3a5f; width: 32px; height: 32px; border-radius: 50% 50% 50% 0; transform: rotate(-45deg); border: 3px solid #d4a017; display: flex; align-items: center; justify-content: center;">
          <div style="transform: rotate(45deg); color: #d4a017; font-weight: bold; font-size: 14px;">M</div>
        </div>`,
        iconSize: [32, 32],
        iconAnchor: [16, 32],
        popupAnchor: [0, -32]
      });
      // Add marker for Clinton, TN
      L.marker([36.1034, -84.1319], {
        icon: customIcon
      }).addTo(map).bindPopup(`
          <div style="text-align: center; padding: 8px;">
            <strong style="color: #1e3a5f; font-size: 14px;">M&M Pipe Services</strong><br/>
            <span style="color: #666;">Clinton, TN</span><br/>
            <a href="tel:8653130028" style="color: #d4a017; font-weight: bold;">865-313-0028</a>
          </div>
        `);
      // Draw service area circle (approximate 150 mile radius to cover North GA)
      L.circle([36.1034, -84.1319], {
        color: '#1e3a5f',
        fillColor: '#1e3a5f',
        fillOpacity: 0.1,
        radius: 240000,
        weight: 2,
        dashArray: '5, 10'
      }).addTo(map);
      mapInstanceRef.current = map;
    }
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);
  return <div className="mt-10 rounded-lg overflow-hidden border border-gray-200 shadow-sm" data-id="element-96">
      <div ref={mapRef} className="h-72 w-full" data-id="element-97" />
      <div className="bg-[#1e3a5f] px-4 py-2 text-center" data-id="element-98">
        <p className="text-white text-sm" data-id="element-99">
          <span className="text-[#d4a017] font-semibold" data-id="element-100">Service Area:</span>{' '}
          East Tennessee, Kentucky, Virginia, North Carolina & North Georgia
        </p>
      </div>
    </div>;
};
export const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission logic here
    console.log('Form submitted:', formData);
    alert('Thank you for your message. We will contact you shortly.');
    setFormData({
      name: '',
      email: '',
      phone: '',
      service: '',
      message: ''
    });
  };
  return <div className="bg-white" data-id="element-101">
      {/* Page Header */}
      <div className="bg-[#1e3a5f] py-20 px-4 text-center relative overflow-hidden" data-id="element-102">
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]" data-id="element-103"></div>
        <h1 className="text-4xl md:text-5xl font-bold text-white uppercase tracking-wider mb-4 relative z-10" data-id="element-104">
          Contact Us
        </h1>
        <div className="w-24 h-1.5 bg-[#d4a017] mx-auto mb-6 relative z-10" data-id="element-105"></div>
        <p className="text-gray-300 max-w-2xl mx-auto text-lg relative z-10" data-id="element-106">
          Get in touch for a quote, emergency service, or to learn more about
          our capabilities.
        </p>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16" data-id="element-107">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16" data-id="element-108">
          {/* Contact Information */}
          <div data-id="element-109">
            <h2 className="text-3xl font-bold text-[#1e3a5f] mb-8" data-id="element-110">
              Get In Touch
            </h2>
            <p className="text-gray-600 mb-10 leading-relaxed" data-id="element-111">
              M&M Pipe Services is ready to assist with your commercial sewer
              and drain needs. We offer 24/7 emergency services for critical
              situations. Call us or fill out the form to get started.
            </p>

            <div className="space-y-8" data-id="element-112">
              <div className="flex items-start" data-id="element-113">
                <div className="bg-[#1e3a5f] p-3 rounded-full mr-4 flex-shrink-0" data-id="element-114">
                  <Phone className="w-6 h-6 text-[#d4a017]" data-id="element-115" />
                </div>
                <div data-id="element-116">
                  <h3 className="text-lg font-bold text-[#1e3a5f] mb-1" data-id="element-117">
                    Phone
                  </h3>
                  <p className="text-gray-600 mb-1" data-id="element-118">24/7 Emergency Service</p>
                  <a href="tel:8653130028" className="text-xl font-bold text-[#d4a017] hover:text-[#b88b14]" data-id="element-119">
                    865-313-0028
                  </a>
                </div>
              </div>

              <div className="flex items-start" data-id="element-120">
                <div className="bg-[#1e3a5f] p-3 rounded-full mr-4 flex-shrink-0" data-id="element-121">
                  <Mail className="w-6 h-6 text-[#d4a017]" data-id="element-122" />
                </div>
                <div data-id="element-123">
                  <h3 className="text-lg font-bold text-[#1e3a5f] mb-1" data-id="element-124">
                    Email
                  </h3>
                  <p className="text-gray-600 mb-1" data-id="element-125">
                    For general inquiries and quotes
                  </p>
                  <a href="mailto:info@mmpipeservices.com" className="text-lg font-medium text-[#1e3a5f] hover:text-[#d4a017]" data-id="element-126">
                    info@mmpipeservices.com
                  </a>
                </div>
              </div>

              <div className="flex items-start" data-id="element-127">
                <div className="bg-[#1e3a5f] p-3 rounded-full mr-4 flex-shrink-0" data-id="element-128">
                  <MapPin className="w-6 h-6 text-[#d4a017]" data-id="element-129" />
                </div>
                <div data-id="element-130">
                  <h3 className="text-lg font-bold text-[#1e3a5f] mb-1" data-id="element-131">
                    Location
                  </h3>
                  <p className="text-gray-600" data-id="element-132">
                    Clinton, TN
                    <br data-id="element-133" />
                    Serving the entire East Tennessee region
                  </p>
                </div>
              </div>

              <div className="flex items-start" data-id="element-134">
                <div className="bg-[#1e3a5f] p-3 rounded-full mr-4 flex-shrink-0" data-id="element-135">
                  <Clock className="w-6 h-6 text-[#d4a017]" data-id="element-136" />
                </div>
                <div data-id="element-137">
                  <h3 className="text-lg font-bold text-[#1e3a5f] mb-1" data-id="element-138">
                    Hours
                  </h3>
                  <p className="text-gray-600" data-id="element-139">
                    <span className="font-semibold" data-id="element-140">Office:</span> Mon-Fri 8:00
                    AM - 5:00 PM
                    <br data-id="element-141" />
                    <span className="font-semibold text-[#d4a017]" data-id="element-142">
                      Emergency:
                    </span>{' '}
                    24 Hours / 7 Days
                  </p>
                </div>
              </div>
            </div>

            {/* Map Placeholder */}
            <ServiceAreaMap data-id="element-143" />
          </div>

          {/* Contact Form */}
          <div className="bg-gray-50 p-8 md:p-10 rounded-2xl shadow-sm border border-gray-100" data-id="element-144">
            <h2 className="text-2xl font-bold text-[#1e3a5f] mb-6" data-id="element-145">
              Send Us a Message
            </h2>
            <form onSubmit={handleSubmit} className="space-y-6" data-id="element-146">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-147">
                <div data-id="element-148">
                  <label htmlFor="name" className="block text-sm font-bold text-gray-700 mb-2" data-id="element-149">
                    Name
                  </label>
                  <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#d4a017] focus:ring-2 focus:ring-[#d4a017]/20 outline-none transition-all" placeholder="Your Name" data-id="element-150" />
                </div>
                <div data-id="element-151">
                  <label htmlFor="phone" className="block text-sm font-bold text-gray-700 mb-2" data-id="element-152">
                    Phone
                  </label>
                  <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} required className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#d4a017] focus:ring-2 focus:ring-[#d4a017]/20 outline-none transition-all" placeholder="(555) 555-5555" data-id="element-153" />
                </div>
              </div>

              <div data-id="element-154">
                <label htmlFor="email" className="block text-sm font-bold text-gray-700 mb-2" data-id="element-155">
                  Email
                </label>
                <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#d4a017] focus:ring-2 focus:ring-[#d4a017]/20 outline-none transition-all" placeholder="your@email.com" data-id="element-156" />
              </div>

              <div data-id="element-157">
                <label htmlFor="service" className="block text-sm font-bold text-gray-700 mb-2" data-id="element-158">
                  Service Needed
                </label>
                <select id="service" name="service" value={formData.service} onChange={handleChange} className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#d4a017] focus:ring-2 focus:ring-[#d4a017]/20 outline-none transition-all bg-white" data-id="element-159">
                  <option value="" data-id="element-160">Select a Service</option>
                  <option value="Manhole Rehabilitation" data-id="element-161">
                    Manhole Rehabilitation
                  </option>
                  <option value="CCTV Inspection" data-id="element-162">
                    CCTV Pipeline Inspection
                  </option>
                  <option value="Hydro Excavation" data-id="element-163">Hydro Excavation</option>
                  <option value="Sewer Cleaning" data-id="element-164">Sewer Line Cleaning</option>
                  <option value="Emergency Service" data-id="element-165">Emergency Service</option>
                  <option value="Other" data-id="element-166">Other</option>
                </select>
              </div>

              <div data-id="element-167">
                <label htmlFor="message" className="block text-sm font-bold text-gray-700 mb-2" data-id="element-168">
                  Message
                </label>
                <textarea id="message" name="message" value={formData.message} onChange={handleChange} required rows={4} className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:border-[#d4a017] focus:ring-2 focus:ring-[#d4a017]/20 outline-none transition-all resize-none" placeholder="Tell us about your project or issue..." data-id="element-169"></textarea>
              </div>

              <button type="submit" className="w-full bg-[#d4a017] hover:bg-[#b88b14] text-[#1e3a5f] font-bold uppercase tracking-wide py-4 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center justify-center" data-id="element-170">
                <Send className="w-5 h-5 mr-2" data-id="element-171" /> Send Message
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>;
};